import { findCommentRepairs } from "@ext/markdown/elements/comment/edit/logic/commentBoundaries";
import type { Range } from "@tiptap/core";
import type { MarkType } from "@tiptap/pm/model";
import { Plugin, PluginKey, type Transaction } from "@tiptap/pm/state";
import type { Step } from "@tiptap/pm/transform";

const commentBoundaryRepairKey = new PluginKey<boolean>("commentBoundaryRepair");

/** What a step touched, in the coordinates right after it; a step that names nothing (a node attribute) touches no text. */
const touchedBy = (step: Step): Range | null => {
	let from = Infinity;
	let to = -Infinity;

	step.getMap().forEach((_oldFrom, _oldTo, newFrom, newTo) => {
		from = Math.min(from, newFrom);
		to = Math.max(to, newTo);
	});
	if (from <= to) return { from, to };

	// a mark step maps no position, so the range it carries already is the range in the new document
	const { from: markFrom, to: markTo } = step as Step & Partial<Range>;
	if (typeof markFrom !== "number" || typeof markTo !== "number") return null;

	return { from: markFrom, to: markTo };
};

/** Everything the transactions touched, as one range in the new document's coordinates. */
const changedRange = (transactions: readonly Transaction[]): Range | null => {
	let from = Infinity;
	let to = -Infinity;

	for (const transaction of transactions) {
		if (from <= to) {
			from = transaction.mapping.map(from, -1);
			to = transaction.mapping.map(to, 1);
		}

		transaction.steps.forEach((step, index) => {
			const touched = touchedBy(step);
			if (!touched) return;

			const rest = transaction.mapping.slice(index + 1);
			from = Math.min(from, rest.map(touched.from, -1));
			to = Math.max(to, rest.map(touched.to, 1));
		});
	}

	return from <= to ? { from, to } : null;
};

/**
 * Keeps a comment from cutting an inline object in half, whichever way the state is reached: a comment
 * over a link, a link inside a comment, a paste, an undo. It is always the comment's boundary that moves.
 * Only the edited part of the document is walked, except on the first edit of the session: the document
 * came from the file without a transaction, so nothing has looked at it yet.
 */
const CommentBoundaryRepair = (commentType: MarkType) =>
	new Plugin<boolean>({
		key: commentBoundaryRepairKey,
		state: {
			init: () => false,
			apply: (transaction, walked) => walked || transaction.docChanged,
		},
		appendTransaction: (transactions, oldState, newState) => {
			if (!transactions.some((transaction) => transaction.docChanged)) return null;

			const walked = commentBoundaryRepairKey.getState(oldState);
			const range = walked ? changedRange(transactions) : { from: 0, to: newState.doc.content.size };
			if (!range) return null;

			const repairs = findCommentRepairs(newState.doc, commentType, range);
			if (!repairs.length) return null;

			const tr = newState.tr;
			repairs.forEach(({ mark, from, to }) => tr.addMark(from, to, mark));
			return tr;
		},
	});

export default CommentBoundaryRepair;
